/**
 * Utility functions for printer integration
 */

/**
 * Send a print request to the local printer service
 * @param {Object} medication - Medication object with name, dosage, etc.
 * @param {Object} prescription - Prescription object with patient info
 * @returns {Promise<Object>} Print result
 */
export const sendPrintRequest = async (medication, prescription) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

  try {
    // Format the date
    const prescriptionDate = prescription.prescriptionDate
      ? new Date(prescription.prescriptionDate).toLocaleDateString()
      : new Date().toLocaleDateString();

    // Build the HOW variable by combining quantity, frequency, and duration
    const howParts = [];
    if (medication.dosage) howParts.push(`QTY: ${medication.dosage}`);
    if (medication.frequency) howParts.push(`FREQ: ${medication.frequency}`);
    if (medication.duration) howParts.push(`DURATION: ${medication.duration}`);
    const how = howParts.join(", ");

    // Build the variables object
    const variables = {
      MEDICINE: medication.medicationName || "",
      HOW: how,
      Patient:
        prescription.patientName ||
        prescription.patientEmail ||
        "Unknown Patient",
      Date: prescriptionDate,
      Doctor:
        prescription.doctorName || prescription.doctorId || "Unknown Doctor",
    };

    // Build the URL
    const printUrl = new URL("http://localhost:11180/api/v1/print");
    printUrl.searchParams.set("design", "prescription");
    printUrl.searchParams.set("variables", JSON.stringify(variables));
    printUrl.searchParams.set("printer", "Brother-USB-000D5G916807");
    printUrl.searchParams.set("copies", "1");

    // Send the print request with timeout
    const response = await fetch(printUrl.toString(), {
      method: "POST",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const result = await response.json();

    return result;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle timeout and network errors
    if (error.name === "AbortError") {
      throw new Error("Print request timed out - printer service may be offline");
    } else if (error.message?.includes("Failed to fetch")) {
      throw new Error("Cannot connect to printer service");
    } else {
      throw error;
    }
  }
};
